class ComparisonSlider extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.slidesCount = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-slider-viewport]');
    this.track = this.querySelector('[data-slider-track]');
    this.slides = this.querySelectorAll('[data-slider-slide]');
    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');
    this.dotsContainer = this.querySelector('[data-slider-dots]');

    if (!this.track || !this.slides.length) return;

    this.slidesCount = this.slides.length;
    this.init();
  }

  init() {
    this.createDots();
    this.attachEventListeners();
    this.updateSlider();
  }

  createDots() {
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.slidesCount; i++) {
      const dot = document.createElement('button');
      dot.classList.add('comparison-slider__dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === 0) dot.classList.add('comparison-slider__dot--active');
      dot.addEventListener('click', () => this.goToSlide(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  attachEventListeners() {
    // Button navigation
    this.prevButton?.addEventListener('click', () => this.prev());
    this.nextButton?.addEventListener('click', () => this.next());

    // Touch events for swipe
    this.track.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this.handleTouchEnd.bind(this));

    // Mouse events for drag (optional, for desktop testing)
    this.track.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.track.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.track.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.track.addEventListener('mouseleave', this.handleMouseUp.bind(this));

    // Prevent default drag behavior on images
    this.slides.forEach(slide => {
      const images = slide.querySelectorAll('img');
      images.forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });
    });
  }

  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.isDragging = true;
    this.startX = e.touches[0].clientX;
    this.track.style.transition = 'none';
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - this.startX;
    const slideWidth = this.viewport.offsetWidth;

    // Calculate the translate value
    this.currentTranslate = this.prevTranslate + diff;

    // Add resistance at boundaries
    const maxTranslate = 0;
    const minTranslate = -(slideWidth * (this.slidesCount - 1));

    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate + (this.currentTranslate - maxTranslate) * 0.3;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate + (this.currentTranslate - minTranslate) * 0.3;
    }

    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  handleTouchEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.touchEndX = e.changedTouches[0].clientX;
    this.track.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex < this.slidesCount - 1) {
        this.next();
      } else if (diff < 0 && this.currentIndex > 0) {
        this.prev();
      } else {
        this.updateSlider();
      }
    } else {
      this.updateSlider();
    }
  }

  handleMouseDown(e) {
    this.isDragging = true;
    this.startX = e.clientX;
    this.track.style.transition = 'none';
    this.track.style.cursor = 'grabbing';
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;

    e.preventDefault();
    const currentX = e.clientX;
    const diff = currentX - this.startX;
    const slideWidth = this.viewport.offsetWidth;

    this.currentTranslate = this.prevTranslate + diff;

    // Add resistance at boundaries
    const maxTranslate = 0;
    const minTranslate = -(slideWidth * (this.slidesCount - 1));

    if (this.currentTranslate > maxTranslate) {
      this.currentTranslate = maxTranslate + (this.currentTranslate - maxTranslate) * 0.3;
    } else if (this.currentTranslate < minTranslate) {
      this.currentTranslate = minTranslate + (this.currentTranslate - minTranslate) * 0.3;
    }

    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    this.track.style.cursor = 'grab';

    const diff = e.clientX - this.startX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff < 0 && this.currentIndex < this.slidesCount - 1) {
        this.next();
      } else if (diff > 0 && this.currentIndex > 0) {
        this.prev();
      } else {
        this.updateSlider();
      }
    } else {
      this.updateSlider();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSlider();
    }
  }

  next() {
    if (this.currentIndex < this.slidesCount - 1) {
      this.currentIndex++;
      this.updateSlider();
    }
  }

  goToSlide(index) {
    if (index >= 0 && index < this.slidesCount) {
      this.currentIndex = index;
      this.updateSlider();
    }
  }

  updateSlider() {
    const slideWidth = this.viewport.offsetWidth;
    const translateX = -this.currentIndex * slideWidth;

    this.prevTranslate = translateX;
    this.currentTranslate = translateX;

    this.track.style.transform = `translateX(${translateX}px)`;

    // Update buttons
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.slidesCount - 1;
    }

    // Update dots
    const dots = this.dotsContainer.querySelectorAll('.comparison-slider__dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('comparison-slider__dot--active', index === this.currentIndex);
    });
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.prevButton?.removeEventListener('click', this.prev);
    this.nextButton?.removeEventListener('click', this.next);
  }
}

// Register the custom element
if (!customElements.get('comparison-slider')) {
  customElements.define('comparison-slider', ComparisonSlider);
}
